import { useCallback, useEffect, useState, useRef } from 'react';
import ReactFlow, {
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  addEdge,
  Connection,
  Edge,
  Node,
  BackgroundVariant,
  Panel,
  ReactFlowInstance,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { nodeTypes } from './WorkflowNodes';
import { StepConfigModal } from './StepConfigModal';
import { TriggerConfigModal } from './TriggerConfigModal';
import { Plus, Save, Play, Trash2 } from 'lucide-react';

interface WorkflowCanvasProps {
  workflowId?: string;
  initialNodes?: Node[];
  initialEdges?: Edge[];
  onSave?: (nodes: Node[], edges: Edge[]) => void;
  readOnly?: boolean;
}

const defaultInitialNodes: Node[] = [
  {
    id: '1',
    type: 'trigger',
    position: { x: 250, y: 25 },
    data: { label: 'Comfort Club Expired', type: 'membership_expired' },
  },
];

export function WorkflowCanvas({
  workflowId,
  initialNodes = defaultInitialNodes,
  initialEdges = [],
  onSave,
  readOnly = false,
}: WorkflowCanvasProps) {
  const reactFlowWrapper = useRef<HTMLDivElement>(null);
  const reactFlowInstance = useRef<ReactFlowInstance | null>(null);
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
  const [selectedNode, setSelectedNode] = useState<Node | null>(null);
  const [showNodeMenu, setShowNodeMenu] = useState(false);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [showTriggerModal, setShowTriggerModal] = useState(false);

  const scheduleFitView = useCallback(() => {
    // Important: In production we observed `.react-flow` computing to height:0px
    // even when its parent had a real height. Forcing a non-zero height (below)
    // plus scheduling fitView after layout ensures nodes render and are visible.
    requestAnimationFrame(() => {
      reactFlowInstance.current?.fitView({ padding: 0.2, duration: 0 });
    });
  }, []);

  // ReactFlow state is initialized only once; when we load a DB-backed workflow we need
  // to sync nodes/edges when initialNodes/initialEdges change.
  useEffect(() => {
    setNodes(initialNodes);
    setEdges(initialEdges);
    setSelectedNode(null);
    setShowNodeMenu(false);
    setShowConfigModal(false);
    setShowTriggerModal(false);

    // Ensure the viewport is reset to show the loaded workflow.
    // (ReactFlow's fitView prop only runs on initial mount.)
    scheduleFitView();
  }, [workflowId, initialNodes, initialEdges, setNodes, setEdges, scheduleFitView]);

  const onConnect = useCallback(
    (params: Connection) => setEdges((eds) => addEdge(params, eds)),
    [setEdges],
  );

  const onNodeClick = useCallback((event: React.MouseEvent, node: Node) => {
    if (!readOnly) {
      setSelectedNode(node);
      if (node.type === 'trigger') {
        setShowTriggerModal(true);
      } else {
        setShowConfigModal(true);
      }
    }
  }, [readOnly]);

  const addNode = useCallback(
    (type: string) => {
      if (readOnly) return;
      const newNode: Node = {
        id: `${nodes.length + 1}`,
        type,
        position: {
          x: Math.random() * 300 + 100,
          y: nodes.length * 150 + 150,
        },
        data: {
          label: getDefaultLabel(type),
          type,
          config: {},
        },
      };
      setNodes((nds) => [...nds, newNode]);
      setShowNodeMenu(false);
      setSelectedNode(newNode);
      setShowConfigModal(true);
    },
    [nodes.length, setNodes, readOnly],
  );

  const deleteNode = useCallback(() => {
    if (readOnly) return;
    if (selectedNode) {
      setNodes((nds) => nds.filter((node) => node.id !== selectedNode.id));
      setEdges((eds) =>
        eds.filter((edge) => edge.source !== selectedNode.id && edge.target !== selectedNode.id)
      );
      setSelectedNode(null);
      setShowConfigModal(false);
    }
  }, [selectedNode, setNodes, setEdges, readOnly]);

  const handleSave = useCallback(() => {
    if (readOnly) return;
    if (onSave) {
      onSave(nodes, edges);
    }
  }, [nodes, edges, onSave, readOnly]);

  return (
    <div
      className="w-full relative bg-gray-50"
      style={{ height: '100%', minHeight: 600 }}
      ref={reactFlowWrapper}
    >
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={readOnly ? undefined : onNodesChange}
        onEdgesChange={readOnly ? undefined : onEdgesChange}
        onConnect={readOnly ? undefined : onConnect}
        onNodeClick={onNodeClick}
        nodeTypes={nodeTypes}
        onInit={(instance) => {
          reactFlowInstance.current = instance;
          scheduleFitView();
        }}
        fitView
        fitViewOptions={{ padding: 0.2 }}
        // Force a non-zero height at the ReactFlow root. In production we observed
        // the `.react-flow` element collapsing to 0px height unless minHeight is set.
        style={{ width: '100%', height: '100%', minHeight: 600 }}
        className="bg-gray-50"
      >
        <Controls />
        <MiniMap />
        <Background variant={BackgroundVariant.Dots} gap={12} size={1} />

        {/* Top Toolbar */}
        {!readOnly && (
          <Panel position="top-left">
            <div className="bg-white rounded-lg shadow-lg p-2 flex gap-2">
              <button
                onClick={() => setShowNodeMenu(!showNodeMenu)}
                className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                <Plus className="w-4 h-4" />
                Add Step
              </button>

              <button
                onClick={handleSave}
                className="flex items-center gap-2 px-3 py-2 bg-green-600 text-white rounded hover:bg-green-700"
              >
                <Save className="w-4 h-4" />
                Save
              </button>

              {selectedNode && (
                <button
                  onClick={deleteNode}
                  className="flex items-center gap-2 px-3 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete
                </button>
              )}
            </div>
          </Panel>
        )}

        {/* Node Menu */}
        {!readOnly && showNodeMenu && (
          <Panel position="top-left" style={{ top: 60 }}>
            <div className="bg-white rounded-lg shadow-xl p-4 w-64">
              <h3 className="font-semibold mb-3">Add Workflow Step</h3>
              <div className="space-y-2">
                <button
                  onClick={() => addNode('email')}
                  className="w-full text-left px-3 py-2 hover:bg-blue-50 rounded flex items-center gap-2"
                >
                  <div className="w-6 h-6 rounded bg-blue-100 flex items-center justify-center">
                    📧
                  </div>
                  <span>Send Email</span>
                </button>

                <button
                  onClick={() => addNode('sms')}
                  className="w-full text-left px-3 py-2 hover:bg-purple-50 rounded flex items-center gap-2"
                >
                  <div className="w-6 h-6 rounded bg-purple-100 flex items-center justify-center">
                    💬
                  </div>
                  <span>Send SMS</span>
                </button>

                <button
                  onClick={() => addNode('ai_call')}
                  className="w-full text-left px-3 py-2 hover:bg-orange-50 rounded flex items-center gap-2"
                >
                  <div className="w-6 h-6 rounded bg-orange-100 flex items-center justify-center">
                    📞
                  </div>
                  <span>AI Call</span>
                </button>

                <button
                  onClick={() => addNode('delay')}
                  className="w-full text-left px-3 py-2 hover:bg-gray-50 rounded flex items-center gap-2"
                >
                  <div className="w-6 h-6 rounded bg-gray-100 flex items-center justify-center">
                    ⏰
                  </div>
                  <span>Wait / Delay</span>
                </button>

                <button
                  onClick={() => addNode('condition')}
                  className="w-full text-left px-3 py-2 hover:bg-yellow-50 rounded flex items-center gap-2"
                >
                  <div className="w-6 h-6 rounded bg-yellow-100 flex items-center justify-center">
                    🔀
                  </div>
                  <span>Condition / Branch</span>
                </button>

                <button
                  onClick={() => addNode('webhook')}
                  className="w-full text-left px-3 py-2 hover:bg-indigo-50 rounded flex items-center gap-2"
                >
                  <div className="w-6 h-6 rounded bg-indigo-100 flex items-center justify-center">
                    🔗
                  </div>
                  <span>Webhook</span>
                </button>

                <button
                  onClick={() => addNode('direct_mail')}
                  className="w-full text-left px-3 py-2 hover:bg-teal-50 rounded flex items-center gap-2"
                >
                  <div className="w-6 h-6 rounded bg-teal-100 flex items-center justify-center">
                    📬
                  </div>
                  <span>Direct Mail (LettrLabs)</span>
                </button>
              </div>
            </div>
          </Panel>
        )}

        {/* Step Configuration Modal */}
        {!readOnly && selectedNode && selectedNode.type !== 'trigger' && showConfigModal && (
          <StepConfigModal
            node={selectedNode}
            isOpen={showConfigModal}
            onClose={() => setShowConfigModal(false)}
            onSave={(nodeId, updatedData) => {
              setNodes((nds) =>
                nds.map((node) =>
                  node.id === nodeId
                    ? {
                        ...node,
                        data: {
                          ...node.data,
                          label: updatedData.label,
                          config: updatedData.config,
                        },
                      }
                    : node
                )
              );
            }}
          />
        )}

        {/* Trigger Configuration Modal */}
        {!readOnly && selectedNode && selectedNode.type === 'trigger' && showTriggerModal && (
          <TriggerConfigModal
            node={selectedNode}
            isOpen={showTriggerModal}
            onClose={() => setShowTriggerModal(false)}
            onSave={(nodeId, updatedData) => {
              setNodes((nds) =>
                nds.map((node) =>
                  node.id === nodeId
                    ? {
                        ...node,
                        data: {
                          ...node.data,
                          label: updatedData.label,
                          config: updatedData.config,
                        },
                      }
                    : node
                )
              );
            }}
          />
        )}
      </ReactFlow>
    </div>
  );
}

function getDefaultLabel(type: string): string {
  switch (type) {
    case 'email':
      return 'Send Email';
    case 'sms':
      return 'Send SMS';
    case 'ai_call':
      return 'Make AI Call';
    case 'delay':
      return 'Wait 3 Days';
    case 'condition':
      return 'Check Condition';
    case 'webhook':
      return 'Call Webhook';
    case 'direct_mail':
      return 'Send Direct Mail';
    default:
      return 'New Step';
  }
}
